import gsap from "gsap";
import { hasSeenIntro, markIntroSeen, resetIntro } from "../../utils/session-intro.js";
import { lockScroll, unlockScroll } from "../../utils/Scroll.js";
import { pageReady } from "../../utils/page-ready.js";
import timeline from "./loader-timeline.js";
import { observeMediaElement } from "../../utils/mediaTelemetry.js";

// ── Dock landing tune ────────────────────────────────────────────────────
// The nav logo is a video whose wordmark sits inside a larger transparent
// frame; the loader wordmark must land on the wordmark, not the frame.
// Fractions measured from Logo_header_poster.webp (406x80, content 12-394 x
// 14-62). Adjust DOCK_SCALE_TWEAK / DOCK_Y_TWEAK to refine the landing.
const NAV_WORDMARK_WIDTH = 0.941; // wordmark width / video frame width
const NAV_WORDMARK_TOP = 0.175;   // wordmark top offset / video frame height
const NAV_WORDMARK_LEFT = 0.0296; // wordmark left offset / video frame width
const DOCK_SCALE_TWEAK = 1;       // multiplier on the final scale
const DOCK_Y_TWEAK = 0;           // px, added to the final Y

// Debug helpers: add ?debug-intro to the URL to force the intro to play
// regardless of localStorage — works on any environment, including
// staging/prod. resetIntro() via the console is DEV-only.
if (import.meta.env.DEV) {
  window.resetIntro = resetIntro;
}

function isForcedByDebugFlag() {
  return new URLSearchParams(window.location.search).has("debug-intro");
}

function log(...args) {
  if (import.meta.env.DEV) console.log("[intro]", ...args);
}

// Linear interpolation over the generated [time, value] sample pairs.
// Samples are baked from the AE keyframes (ease + time remap included),
// so lerp between them reproduces the exact curves.
function sampleAt(samples, t) {
  if (t <= samples[0][0]) return samples[0][1];
  const last = samples[samples.length - 1];
  if (t >= last[0]) return last[1];
  let lo = 0;
  let hi = samples.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (samples[mid][0] <= t) lo = mid;
    else hi = mid;
  }
  const [t0, v0] = samples[lo];
  const [t1, v1] = samples[hi];
  return v0 + ((t - t0) / (t1 - t0)) * (v1 - v0);
}

export default class Loader {
  constructor(el) {
    this.el = el;
    this.logo = el.querySelector(".c-loader__logo");
    this.svg = this.logo.querySelector("svg");
    this.videoWrap = el.querySelector("[data-sigle-video]");
    this.video = this.videoWrap.querySelector("video");
    if (this.video) {
      observeMediaElement(this.video, { context: "loader", kind: "sigle", src: this.video.currentSrc || this.video.src });
    }
    this.posterImg = el.querySelector("[data-loader-poster]");
    this.navLogo = document.querySelector(".c-navigation__logo");
    this.videoDead = false;
    this.posterReady = false;

    this.letters = timeline.letters.map((track) => ({
      ...track,
      path: this.svg.querySelector(`[data-letter="${track.glyph}"]`),
    }));
    this.siglePath = this.svg.querySelector('[data-letter="sigle"]');

    // The page counts as ready a moment before the loader is fully gone (see page-ready.js).
    this.readyBlock = pageReady.block("loader");

    // Decided while parsing by the inline script in loader.twig (mobile, in-app
    // browser, campaign landing, seen this week). Same weekly check here as a
    // fallback if that script didn't run.
    const decision = window.vsIntro ?? {
      play: isForcedByDebugFlag() || !hasSeenIntro(),
      reason: "seen this week",
    };
    if (!decision.play) {
      log(`skipped — ${decision.reason}`);
      this.skip();
      return;
    }

    log(isForcedByDebugFlag() ? "forced via ?debug-intro" : "playing — not seen in the last week");
    this.setup();
    this.playIntro();
  }

  // Already seen this week: the loader's black
  // backdrop is still visible by default while the JS bundle loads, but the
  // logo never was (see .c-loader__logo default opacity in the SCSS) — so
  // there's nothing to jump. Just fade the backdrop out instead of an
  // instant removal, which reads as a jarring pop.
  skip() {
    this.readyBlock.releaseIn(0.3);
    gsap.to(this.el, {
      autoAlpha: 0,
      duration: 0.3,
      ease: "power1.out",
      onComplete: () => this.el.remove(),
    });
  }

  setup() {
    log("setup");
    gsap.set(this.el, { autoAlpha: 1 });
    // Hide the real nav logo until the loader wordmark docks onto it,
    // otherwise both are visible during the dock (page is revealed by then).
    if (this.navLogo) gsap.set(this.navLogo, { autoAlpha: 0 });
    lockScroll();

    // Letters start below the viewport (their sampled dy at t=0).
    this.updateSvgScale();
    for (const letter of this.letters) {
      gsap.set(letter.path, { y: this.dyUser(letter, 0) });
    }
    // Only reveal the logo once it's positioned at the timeline's first
    // frame (the SCSS hides it by default) — otherwise it flashes at its
    // untransformed rest position, then jumps once this loop runs.
    gsap.set(this.logo, { autoAlpha: 1 });

    // The sigle slot is the video until the switch time; the flat path takes
    // over afterwards. If the video can't play (missing asset, autoplay
    // refusal), fall back to the path for the whole intro.
    gsap.set(this.siglePath, { autoAlpha: 0 });
    this.positionSigleVideo();

    // Poster of the nav video: swapped in for the svg at the cut so the
    // final handoff compares identical pixels with the nav video's frame 0.
    if (this.posterImg) {
      const posterOk = () => {
        this.posterReady = true;
        this.positionPoster();
      };
      if (this.posterImg.complete && this.posterImg.naturalWidth > 0) posterOk();
      else {
        this.posterImg.addEventListener("load", posterOk, { once: true });
        this.posterImg.addEventListener("error", () => log("poster missing — keeping svg for the dock"), { once: true });
      }
    }

    this.onResize = () => {
      this.updateSvgScale();
      this.positionSigleVideo();
      if (this.posterReady) this.positionPoster();
    };
    window.addEventListener("resize", this.onResize);

    this.video.addEventListener("error", () => this.killVideo("video failed to load"), { once: true });
    // 404s on <source> children don't fire the video's own error event
    const lastSource = this.video.querySelector("source:last-of-type");
    lastSource?.addEventListener("error", () => this.killVideo("no playable source"), { once: true });
    if (this.video.readyState === 0 && this.video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) {
      this.killVideo("no playable source");
      return;
    }
    const playing = this.video.play();
    if (playing) playing.catch(() => this.killVideo("video autoplay refused"));
  }

  killVideo(reason) {
    if (this.videoDead) return;
    this.videoDead = true;
    log("sigle fallback —", reason);
    gsap.set(this.videoWrap, { autoAlpha: 0 });
    gsap.set(this.siglePath, { autoAlpha: 1 });
  }

  // CSS transforms on SVG children are applied in the viewBox's user-unit
  // space, scaled by the svg's render size — pixel values must be divided by
  // this scale before being set on <path> elements.
  // Also caches the viewport height for dyCss: reading window.innerHeight in
  // the per-frame onUpdate, right after moving the letters, forced a layout on
  // every frame of the intro.
  updateSvgScale() {
    this.svgScale = this.svg.getBoundingClientRect().width / this.svg.viewBox.baseVal.width;
    this.viewportH = window.innerHeight;
  }

  // dy for a letter at master time t, converted from comp-normalized units
  // (1.0 == one AE comp height) to CSS px. The AE comp is a 1920x1080
  // viewport mock, so comp height maps to viewport height.
  dyCss(letter, t) {
    return sampleAt(letter.samples, t) * this.viewportH;
  }

  // same travel expressed in SVG user units, for the <path> transforms
  dyUser(letter, t) {
    return this.dyCss(letter, t) / this.svgScale;
  }

  // Size/position the poster so its wordmark overlays the svg wordmark
  // exactly (untransformed layout coords; the dock transform on the parent
  // then carries it into the nav position, where the poster frame matches
  // the nav video frame by construction).
  positionPoster() {
    const logoW = this.logo.offsetWidth;
    const imgW = logoW / NAV_WORDMARK_WIDTH;
    const ratio = this.posterImg.naturalHeight / this.posterImg.naturalWidth;
    const imgH = imgW * ratio;
    gsap.set(this.posterImg, {
      width: imgW,
      left: -imgW * NAV_WORDMARK_LEFT,
      top: -imgH * NAV_WORDMARK_TOP,
    });
  }

  positionSigleVideo() {
    // Overlay the video on the sigle path's rendered box (with some air for
    // the animation to overflow the flat shape).
    const svgRect = this.svg.getBoundingClientRect();
    const box = this.siglePath.getBBox();
    const scale = svgRect.width / this.svg.viewBox.baseVal.width;
    const pad = 0.35; // extra room around the flat shape, fraction of its size
    const w = box.width * scale;
    const h = box.height * scale;
    gsap.set(this.videoWrap, {
      width: w * (1 + pad * 2),
      height: h * (1 + pad * 2),
      left: box.x * scale - w * pad,
      top: box.y * scale - h * pad,
    });
    this.sigleRestY = 0; // wrapper is positioned at rest; dy applied as translate
  }

  // The full choreography, driven by the generated AE timeline. One master
  // timeline; letters + video follow their baked sample curves each frame.
  playIntro() {
    log("animation start");
    const data = timeline;
    const sigleTrack = this.letters.find((l) => l.glyph === "sigle");

    const master = gsap.timeline({
      onComplete: () => this.onIntroComplete(),
      onUpdate: () => {
        const t = master.time();
        for (const letter of this.letters) {
          gsap.set(letter.path, { y: this.dyUser(letter, t) });
        }
        // the video rides the sigle/flag motion (parented in AE); it's an
        // HTML wrapper, so it moves in real CSS px
        if (!this.videoDead) {
          gsap.set(this.videoWrap, { y: this.dyCss(sigleTrack, t) });
        }
      },
    });

    // Drives the clock; all motion happens in onUpdate from the samples.
    master.to({}, { duration: data.duration, ease: "none" }, 0);

    if (import.meta.env.DEV) {
      window.__introTl = master; // inspect/scrub the intro from the console
    }

    // The cut: in AE the letters layer set hard-swaps to the flat logo over
    // the page mock. Web equivalent: swap the whole svg for the nav video's
    // poster frame (already the final dark look, and pixel-identical to the
    // video's frame 0, so the end handoff has nothing to jump to). Happens
    // mid-collapse, too fast to be seen.
    master.add(() => {
      log("cut — revealing page, swapping to poster");
      this.el.style.pointerEvents = "none";
      this.el.classList.add("is-docking");

      if (this.posterReady) {
        if (!this.videoDead) this.video.pause();
        gsap.set([this.svg, this.videoWrap], { autoAlpha: 0 });
        gsap.set(this.posterImg, { autoAlpha: 1 });
      } else {
        // poster missing: keep the svg, recolor it and show the flat sigle
        if (!this.videoDead) {
          gsap.to(this.videoWrap, { autoAlpha: 0, duration: 0.15, overwrite: "auto" });
          gsap.to(this.siglePath, { autoAlpha: 1, duration: 0.15, overwrite: "auto" });
          this.video.pause();
        }
        if (this.navLogo) {
          gsap.to(this.logo, {
            color: getComputedStyle(this.navLogo).color,
            duration: 0.2,
            ease: "power1.inOut",
          });
        }
      }
      // scroll stays locked until onIntroComplete: unlocking here can bring
      // the scrollbar back mid-dock, shifting the nav logo target sideways.
    }, data.videoSwitch);

    // Extra playback speed on top of the baked timeline. The dock tween is
    // parented on master (see buildDock), so timeScale carries it too; the
    // sigle video is sped up to match so it stays in sync through the cut.
    const speedUp = 1.2;
    master.timeScale(speedUp);
    this.video.playbackRate = speedUp;

    this.buildDock(master);
    this.readyBlock.releaseIn(master.totalDuration() / master.timeScale());
  }

  // Dock move: quick top-anchored collapse into the nav logo's exact box,
  // shaped by the progress curve measured from the AE render. Top edge and
  // scale share the curve; endpoints come from the real DOM.
  buildDock(master) {
    const dock = timeline.dock;
    let from;
    const proxy = { t: dock.start };

    // Measured lazily when the master playhead reaches dock.start, not at
    // build time — layout can still shift before then (late images, etc.).
    master.add(() => {
      from = this.svg.getBoundingClientRect();
    }, dock.start);

    // Parented on master (not a standalone gsap.to) so it inherits
    // master.timeScale() and stays in sync with onIntroComplete.
    master.to(proxy, {
      t: dock.end,
      duration: dock.end - dock.start,
      ease: "none",
      onUpdate: () => {
        // target re-measured every frame: layout can shift under the overlay
        // (scrollbar, late images) and the landing must stay pixel-exact.
        // The landing box is the wordmark INSIDE the video frame.
        const box = this.navLogo
          ? this.navLogo.getBoundingClientRect()
          : { left: from.left, top: from.top, width: from.width, height: 0 };
        const targetW = box.width * NAV_WORDMARK_WIDTH;
        const targetTop = box.top + box.height * NAV_WORDMARK_TOP;

        const endScale = (targetW / from.width) * DOCK_SCALE_TWEAK;
        const endX = box.left + box.width / 2 - (from.left + from.width / 2);
        const endYTop = targetTop - from.top + DOCK_Y_TWEAK;

        const p = sampleAt(dock.progress, proxy.t);
        gsap.set(this.logo, {
          x: endX * p,
          y: endYTop * p,
          scale: 1 + (endScale - 1) * p,
          transformOrigin: "50% 0%",
        });
      },
    }, dock.start);
  }

  onIntroComplete() {
    log("animation complete — crossfading to the nav logo");
    this.readyBlock.release(); // no-op if the lead already released it
    markIntroSeen();
    unlockScroll();
    window.removeEventListener("resize", this.onResize);
    if (!this.navLogo) {
      this.el.remove();
      return;
    }
    // quick crossfade docked wordmark -> real nav logo, then drop the overlay
    gsap.to(this.navLogo, { autoAlpha: 1, duration: 0, ease: "none" });
    gsap.to(this.logo, {
      autoAlpha: 0,
      // delay: 0.1,
      duration: 0,
      ease: "none",
      onComplete: () => {
        gsap.set(this.navLogo, { clearProps: "opacity,visibility" });
        this.el.remove();
      },
    });
  }

}
